import { getChildrenFinder, getDescendantPaths } from '@paragon/layout-models';

import { SortableColumnName } from '../grid/ordering';
import { getRangeBetween } from '../grid/range';
import { getVisiblePageRowIds } from '../selectors/projectGridRows';
import {
  allRowsCollapsed,
  pageChanged,
  rowCheckToggled,
  rowCollapsed,
  rowExpanded,
  rowsChecked,
  rowsCollapsed,
  rowSelected,
  rowsExpanded,
  rowsUnchecked,
  sortColumnToggled,
} from '../slices/gridSlice';
import { ThunkActionVoid } from './thunkActions';

export const expandRow = (expandedRowPath: string) => rowExpanded({ rowPath: expandedRowPath, descendants: [] });

export const expandRows = (expandedRowPaths: string[]) => rowsExpanded({ rowPaths: expandedRowPaths, descendants: [] });

export const collapseRow =
  (collapsedRowPath: string): ThunkActionVoid =>
  (dispatch, getState) => {
    dispatch(
      rowCollapsed({
        rowPath: collapsedRowPath,
        descendants: getDescendantPaths(collapsedRowPath, getChildrenFinder(getState())),
      }),
    );
  };

export const collapseRows =
  (collapsedRowPaths: string[]): ThunkActionVoid =>
  (dispatch, getState) => {
    const descendants = collapsedRowPaths.flatMap((rowPath) =>
      getDescendantPaths(rowPath, getChildrenFinder(getState())),
    );
    dispatch(rowsCollapsed({ rowPaths: collapsedRowPaths, descendants }));
  };

export const collapseAllRows = () => allRowsCollapsed();

export const checkRows =
  (checkedRowPaths: readonly string[]): ThunkActionVoid =>
  (dispatch, getState) => {
    const descendants = checkedRowPaths.flatMap((rowPath) =>
      getDescendantPaths(rowPath, getChildrenFinder(getState())),
    );
    dispatch(rowsChecked({ rowPaths: checkedRowPaths, descendants }));
  };

export const uncheckRows =
  (uncheckedRowPaths: string[]): ThunkActionVoid =>
  (dispatch, getState) => {
    const descendants = uncheckedRowPaths.flatMap((rowPath) =>
      getDescendantPaths(rowPath, getChildrenFinder(getState())),
    );
    dispatch(rowsUnchecked({ rowPaths: uncheckedRowPaths, descendants }));
  };

export const selectModel = (rowPath: string, parentGuid: string | null) => rowSelected({ rowPath, parentGuid });

export const toggleSortColumn = (columnName: SortableColumnName) => sortColumnToggled({ columnName });

export const setPageIndex = (pageIndex: number) => pageChanged({ pageIndex });

export const toggleRowChecked =
  (rowPath: string): ThunkActionVoid =>
  (dispatch, getState) => {
    dispatch(
      rowCheckToggled({
        rowPath,
        descendants: getDescendantPaths(rowPath, getChildrenFinder(getState())),
      }),
    );
  };

/**
 * Handles a click on a row checkbox: a plain click toggles the row, while a shift-click checks the whole range from the
 *   last individually-checked row (the anchor) to this one.
 */
export const rowCheckboxClicked =
  (rowPath: string, shiftKey: boolean): ThunkActionVoid =>
  (dispatch, getState) => {
    const checkAnchor = getState().grid.checkAnchor;

    if (shiftKey && checkAnchor) {
      const range = getRangeBetween(getVisiblePageRowIds(getState()), checkAnchor, rowPath);
      if (range) {
        dispatch(checkRows(range));
        return;
      }
    }

    dispatch(toggleRowChecked(rowPath));
  };
